/**
 * The `metadata` a turn carries, which is how this client says anything at all
 * beyond the sentence somebody typed.
 *
 * Three keys, and all three are REQUIRED on every request — `mentions`, `actions`
 * and `route`. Every `X | None` field in this API is declared without a default,
 * so the key must be present even when the value is empty; omitting one is a 400
 * rather than a no-op.
 *
 * ── `mentions` is what grounds a question, and `route` is not ─────────────
 * `{module, module_id}`. `@Logbook` is `{module: 'logbook', module_id: null}` — the
 * question is about the log at large. `#LOG-26` is `{module: 'logbook', module_id:
 * <uuid>}` — the uuid, never the serial, which is only what the chip shows.
 *
 * The engine reduces `route` to its first path segment and DISCARDS the id, so a
 * question asked on an anomaly with no mention against it is a question about
 * nothing in particular. The mention is the grounding; the route is context.
 *
 * ── `actions` are requests, not results ───────────────────────────────────
 * `request_rca` on an anomaly is what starts an analysis — there is no endpoint
 * for it. A 201 means the request was RECORDED, never that it was understood: the
 * engine reads it off the stored row.
 */

/** The server's list cap (`request_list_max_items`) is generous; this is a sanity bound. */
const MAX_MENTIONS = 20

/** One entry per (module, module_id), first occurrence kept. */
export function dedupeMentions(mentions) {
  const seen = new Set()
  const out = []
  for (const mention of mentions ?? []) {
    if (!mention?.module) continue
    const key = `${mention.module}:${mention.module_id ?? ''}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ module: mention.module, module_id: mention.module_id ?? null })
  }
  return out.slice(0, MAX_MENTIONS)
}

/**
 * The actions a turn may request — `CopilotRequestedAction`'s allowed pairs, and
 * every other (type, module) is a 400. `request_rca` is anomalies only; `update`
 * is anomalies, work orders, the log and handovers; `create` is the last three.
 * There is no `delete`.
 */
const REQUESTABLE = {
  anomalies: ['request_rca', 'update'],
  work_orders: ['create', 'update'],
  logbook: ['create', 'update'],
  shift_handovers: ['create', 'update'],
}

/**
 * One requested action. `create` carries `module_id: null` — the record does not
 * exist yet — and `update` and `request_rca` carry the id of the record on screen,
 * which is how the engine knows which one is meant.
 */
export function requestedAction(type, module, moduleId = null) {
  if (!REQUESTABLE[module]?.includes(type)) throw new Error(`${type} is not a requestable action on ${module}`)
  const module_id = type === 'create' ? null : moduleId
  if (type !== 'create' && !module_id) throw new Error(`${type} on ${module} needs the record's id`)
  return { type, module, module_id }
}

/** `request_rca` on one anomaly. `module` must be spelled; the engine skips any other. */
export const rcaAction = (anomalyId) => requestedAction('request_rca', 'anomalies', anomalyId)

/**
 * @param mentions {module, module_id}[] — page context and anything typed
 * @param actions  requested actions, e.g. `rcaAction(id)`
 * @param route    `routeOf(pathname)`; never empty
 */
export function turnMetadata({ mentions = [], actions = [], route }) {
  return {
    mentions: dedupeMentions(mentions),
    actions,
    route: route || 'home',
  }
}
